/**
 * **A clip, and what it is about, side by side** — the new clip with its set, or the client's
 * question beside the film it was asked under.
 *
 * On the `/zadania` desk the film plays IN the card (owner, 25 September 2026): the page's small
 * player (`TaskSlots.clipPlayer`, `features/clip-player`) with play and pause, the progress line,
 * a reply to the whole recording and „Analizuj" into the full player. Starting it marks the clip seen
 * on its first frame, exactly as the full player does (`exercise-clip-timeline` R9/R18), and a reply
 * is the thread's own write — so both close their item the way the full player's do.
 *
 * Everywhere else the frame is the film's own shape (`ClipFrame`) and a door into the full player on
 * the client's card. On the Pulpit's card it is a small poster on the body's left, with the meta line
 * and the question beside it, under the same head every kind has (owner, 26 September 2026).
 */

import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { clipAspect, formatClock } from '~/entities/exercise-clip';

import type { TaskClip, TaskDensity } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { clipDestination } from '../model/taskAction';
import { ClipFrame } from './ClipFrame';
import styles from './taskBlocks.module.css';

interface ClipBlockProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  /** The meta line the Pulpit's card moves from its head into the column beside the poster. */
  readonly glance: string;
  /** The page's own way to the player, where it hosts one (`TaskSlots.openClip`). */
  readonly openClip: ((clipId: number) => void) | undefined;
  /** The page's small player (`TaskSlots.clipPlayer`), where the desk plays the film in place. */
  readonly player: ((clip: TaskClip) => ReactNode) | undefined;
}

export function ClipBlock({ item, density, glance, openClip, player }: ClipBlockProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detail = item.detail;
  const action = item.action;
  const question = item.description.values.preview;
  const open =
    action.kind === 'openClip'
      ? () => {
          if (openClip === undefined) void navigate(clipDestination(action));
          else openClip(action.clipId);
        }
      : null;

  const glanceLine = glance === '' ? null : <p className={styles.glanceLine}>{glance}</p>;
  const inPlace = density === 'desk' && player !== undefined && item.subjectCustomerId !== null ? player : null;
  const clientId = item.subjectCustomerId;

  if (detail.kind === 'clip') {
    if (inPlace !== null && clientId !== null) {
      return (
        <div className={styles.stack}>
          {inPlace({
            clientId,
            clipId: detail.clipId,
            aspect: clipAspect(detail),
            startAt: null,
            label: detail.exerciseName,
            aside: null,
          })}
        </div>
      );
    }
    return (
      <div className={`${String(styles.clip)} ${String(styles[density])} ${density === 'desk' ? String(styles.clipStack) : ''}`}>
        <ClipFrame
          clipId={detail.clipId}
          aspect={clipAspect(detail)}
          duration={detail.durationSeconds === null ? '' : formatClock(detail.durationSeconds, false, i18n.language)}
          density={density}
          onOpen={open}
        />
        {glanceLine === null ? null : <div className={styles.clipSide}>{glanceLine}</div>}
      </div>
    );
  }

  if (detail.kind === 'clipComment') {
    const asked =
      typeof question === 'string' ? (
        <p className={styles.question}>
          <q>{question}</q>
        </p>
      ) : null;
    if (inPlace !== null && clientId !== null && detail.clipId !== null) {
      return (
        <div className={styles.stack}>
          {inPlace({
            clientId,
            clipId: detail.clipId,
            aspect: clipAspect({ width: null, height: null }),
            startAt: detail.offsetMs === null ? null : detail.offsetMs / 1000,
            label: t('web:forma.dock.player'),
            aside: asked,
          })}
        </div>
      );
    }
    return (
      <div className={`${String(styles.clip)} ${String(styles[density])}`}>
        {detail.clipId === null ? null : (
          <ClipFrame
            clipId={detail.clipId}
            // The comment's payload carries no shape; the portrait phone every capture path produces.
            aspect={clipAspect({ width: null, height: null })}
            duration=""
            density={density}
            onOpen={open}
          />
        )}
        {asked === null && glanceLine === null ? null : (
          <div className={styles.clipSide}>
            {glanceLine}
            {asked}
          </div>
        )}
      </div>
    );
  }

  return null;
}
